// TanStack Query keys and hooks for compose drafts. A draft's send is an executor work item, so
// the `work` SSE event refreshes these (see feedEvents.ts); the intervals are a safety net.
import { queryOptions, useQuery, type QueryClient } from "@tanstack/react-query";
import type { ComposeDraftState, ComposeDraftView } from "@shared/api";
import { composeApi, type ComposeListParams } from "./composeApi";

/** The states the Drafts list shows: still editable, waiting to send, or in need of a decision. */
export const OPEN_DRAFT_STATES: readonly ComposeDraftState[] = ["draft", "approved", "failed"];

export const composeKeys = {
  all: ["compose"] as const,
  list: (params: ComposeListParams) => ["compose", "list", params] as const,
  draft: (id: string) => ["composeDraft", id] as const,
  identities: (accountId: string) => ["composeIdentities", accountId] as const,
  /** Keyed by revision: a draft that changed must be previewed (and armed) again. */
  preview: (id: string, rev: number) => ["composePreview", id, rev] as const,
};

export function composeListQuery(params: ComposeListParams) {
  return queryOptions({
    queryKey: composeKeys.list(params),
    queryFn: ({ signal }) => composeApi.list(params, signal),
    staleTime: 15_000,
    refetchInterval: 120_000,
  });
}

export function useComposeDrafts(params: ComposeListParams, enabled = true) {
  return useQuery({ ...composeListQuery(params), enabled });
}

export function composeDraftQuery(id: string) {
  return queryOptions({
    queryKey: composeKeys.draft(id),
    queryFn: ({ signal }) => composeApi.get(id, signal),
    staleTime: 10_000,
  });
}

export function useComposeDraft(id: string | null) {
  return useQuery({ ...composeDraftQuery(id ?? ""), enabled: !!id });
}

export function useComposeIdentities(accountId: string | null) {
  return useQuery({
    queryKey: composeKeys.identities(accountId ?? ""),
    queryFn: ({ signal }) => composeApi.identities(accountId ?? "", signal),
    enabled: !!accountId,
    staleTime: 300_000,
  });
}

export function useComposePreview(id: string | null, rev: number, enabled: boolean) {
  return useQuery({
    queryKey: composeKeys.preview(id ?? "", rev),
    queryFn: ({ signal }) => composeApi.preview(id ?? "", signal),
    enabled: enabled && !!id,
    retry: false,
    staleTime: 0,
    gcTime: 30_000,
  });
}

/** Put a server draft into the single-draft cache and every cached list that holds it. */
export function acceptDraft(queryClient: QueryClient, draft: ComposeDraftView): void {
  queryClient.setQueryData(composeKeys.draft(draft.id), draft);
  queryClient.setQueriesData<ComposeDraftView[]>({ queryKey: ["compose", "list"] }, (list) =>
    list?.map((d) => (d.id === draft.id ? draft : d)),
  );
}

export function invalidateCompose(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: composeKeys.all });
  void queryClient.invalidateQueries({ queryKey: ["composeDraft"] });
}
